import circleMarkerElectraRaw from '@/assets/icons/circle-marker-electra.svg?raw';
import externalLinkRaw from '@/assets/icons/external-link.svg?raw';
import { cn } from '@/libs/utils/cn';
import {
  CPO_ELECTRA,
  MAP_BUBBLE_LABEL,
  MAP_BUBBLE_ROW,
  MAP_BUBBLE_SURFACE,
  MAP_BUBBLE_TITLE,
  MAP_BUBBLE_VALUE,
} from '../constants';
import type { StationPopupData } from '../hooks/useStationsInteractions';
import { getGoogleMapStreetViewUrl } from '../utils/getGoogleMapStreetViewUrl';
import { getStationByCharges } from '../utils/getStationByCharges';

/**
 * Portalled into the live Mapbox popup (see `useStationsInteractions`). Station
 * properties stay React text nodes: `cpo` is replicated third-party data.
 *
 * Dressed as a map bubble, the same surface the zone bubble and the traffic
 * tooltip use — three layers of one map, one card treatment.
 */
export function StationMapPopup({ station }: { station: StationPopupData }) {
  const isElectra = station.cpo === CPO_ELECTRA;
  const icon = isElectra ? circleMarkerElectraRaw : getStationByCharges(station.charges).icons.icon;

  return (
    <div className={cn('mb-3 w-56', MAP_BUBBLE_SURFACE)}>
      <div className={cn('flex items-center gap-1.5', MAP_BUBBLE_TITLE)}>
        <div
          className="size-5 [&>svg]:size-full"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG from static assets
          dangerouslySetInnerHTML={{ __html: icon }}
        />
        {isElectra ? 'Electra charging station' : 'Competitor station'}
      </div>
      <a
        href={getGoogleMapStreetViewUrl(station.lngLat.lat, station.lngLat.lng)}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-0.5 flex items-center gap-1 text-brand-800 hover:underline"
      >
        See street view
        <div
          className="size-3.5 [&>svg]:stroke-3"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG from static assets
          dangerouslySetInnerHTML={{ __html: externalLinkRaw }}
        />
      </a>
      <StationMetric label="Avg. sessions per day" value={station.charges?.toFixed(1)} />
      <StationMetric label="Total HPC spots" value={station.operativeHpcSpots || undefined} />
      <StationMetric label="Network" value={station.cpo} />
    </div>
  );
}

function StationMetric({ label, value }: { label: string; value?: string | number }) {
  return (
    <div className={MAP_BUBBLE_ROW}>
      <span className={MAP_BUBBLE_LABEL}>{label}</span>
      <span className={cn('truncate capitalize', MAP_BUBBLE_VALUE)}>{value ?? 'No data'}</span>
    </div>
  );
}
